import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Truck, DollarSign, ShoppingBag, FileText, CheckCircle, Shield } from 'lucide-react';
import api from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils-helpers';

export default function DriverDetail360() {
  const { driverId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(`/admin/drivers/${driverId}/360`);
        setData(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [driverId]);

  if (loading) return <div className="p-6">Loading...</div>;
  if (!data) return <div className="p-6">Driver not found</div>;

  const driver = data.driver;
  const stats = data.statistics;

  const getBgcStatusColor = (status) => {
    const colors = {
      cleared: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-blue-100 text-blue-800',
      failed: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/admin/drivers')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold" data-font="display">360° Driver View</h1>
            <p className="text-muted-foreground">Complete delivery profile for {driver.name}</p>
          </div>
        </div>
      </div>

      {/* Profile Header */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex gap-6">
              <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
                <Truck className="h-12 w-12 text-primary" />
              </div>
              <div className="space-y-3">
                <div>
                  <div className="flex items-center gap-3">
                    <h2 className="text-2xl font-bold">{driver.name}</h2>
                    {driver.driver_number && (
                      <Badge 
                        variant="outline" 
                        className="font-mono text-xs border-[hsl(351,96%,31%)] text-[hsl(351,96%,31%)] bg-[hsl(351,96%,31%,0.05)]"
                        data-testid="driver-id-badge"
                      >
                        {driver.driver_number}
                      </Badge>
                    )}
                  </div>
                  <p className="text-muted-foreground">{driver.email}</p>
                  {driver.phone && <p className="text-muted-foreground">{driver.phone}</p>}
                </div>
                <div className="flex gap-2">
                  <Badge className={getBgcStatusColor(driver.bgc_status || 'pending')}>
                    BGC: {driver.bgc_status || 'pending'}
                  </Badge>
                  {driver.is_verified && <Badge className="bg-green-100 text-green-800">Verified</Badge>}
                  {driver.is_online && <Badge className="bg-blue-100 text-blue-800">● Online</Badge>}
                  {driver.ready_to_accept && <Badge className="bg-emerald-100 text-emerald-800">✓ Ready to Accept</Badge>}
                </div>
                {driver.vehicle_type && (
                  <p className="text-sm text-muted-foreground">
                    Vehicle: {driver.vehicle_type} {driver.license_number && `(${driver.license_number})`}
                  </p>
                )}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-4 text-right">
              <div>
                <p className="text-2xl font-bold">{stats.total_deliveries}</p>
                <p className="text-sm text-muted-foreground">Deliveries</p>
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.completed_deliveries}</p>
                <p className="text-sm text-muted-foreground">Completed</p>
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.active_deliveries}</p>
                <p className="text-sm text-muted-foreground">Active</p>
              </div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(stats.total_earnings)}</p>
                <p className="text-sm text-muted-foreground">Earnings</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="deliveries" className="w-full">
        <TabsList>
          <TabsTrigger value="deliveries"><Truck className="h-4 w-4 mr-2" />Deliveries</TabsTrigger>
          <TabsTrigger value="earnings"><DollarSign className="h-4 w-4 mr-2" />Earnings</TabsTrigger>
          <TabsTrigger value="documents"><FileText className="h-4 w-4 mr-2" />Documents</TabsTrigger>
        </TabsList>

        <TabsContent value="deliveries" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Delivery History</CardTitle>
            </CardHeader>
            <CardContent>
              {data.deliveries.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No deliveries found</p>
              ) : (
                <div className="space-y-3">
                  {data.deliveries.map(delivery => (
                    <div key={delivery.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="font-medium">{delivery.id}</p>
                        <p className="text-sm text-muted-foreground">{delivery.vendor_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(delivery.created_at)}
                          {delivery.delivered_at && ` → ${formatDate(delivery.delivered_at)}`}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{formatCurrency(delivery.delivery_fee || 0)}</p>
                        <Badge className={delivery.status === 'delivered' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}>
                          {delivery.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="earnings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Earnings History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {data.transactions.map(txn => (
                  <div key={txn.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <p className="font-medium">Delivery Payment</p>
                      <p className="text-sm text-muted-foreground">{txn.description || 'No description'}</p>
                      <p className="text-xs text-muted-foreground">{formatDate(txn.created_at)}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-green-600">+{formatCurrency(txn.amount)}</p>
                      <Badge className={txn.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                        {txn.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documents" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Compliance Documents</CardTitle>
            </CardHeader>
            <CardContent>
              {!data.documents || data.documents.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No documents uploaded</p>
              ) : (
                <div className="space-y-3">
                  {data.documents.map((doc, idx) => (
                    <div key={doc.id || idx} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="h-8 w-8 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{doc.document_type}</p>
                          <p className="text-sm text-muted-foreground">{doc.document_name || 'Document'}</p>
                          <p className="text-xs text-muted-foreground">
                            Uploaded: {formatDate(doc.uploaded_at || doc.created_at)}
                          </p>
                        </div>
                      </div>
                      <Badge className={doc.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                        {doc.status || 'pending'}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bgverification" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" /> Background Verification
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">BGC Status</p>
                  <Badge className={data.bgc_status === 'cleared' ? 'bg-green-100 text-green-800' : data.bgc_status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}>
                    {data.bgc_status || 'Not Initiated'}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Last Check Date</p>
                  <p className="font-medium">{data.bgc_last_check ? formatDate(data.bgc_last_check) : 'N/A'}</p>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Background Check History</h4>
                {data.bgc_history && data.bgc_history.length > 0 ? (
                  <div className="space-y-2">
                    {data.bgc_history.map((check, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-muted/50 rounded">
                        <div>
                          <p className="text-sm font-medium">{check.check_type}</p>
                          <p className="text-xs text-muted-foreground">{formatDate(check.date)}</p>
                        </div>
                        <Badge variant={check.result === 'passed' ? 'default' : 'destructive'}>
                          {check.result}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">No background checks performed yet</p>
                )}
              </div>

              <div className="border-t pt-4">
                <p className="text-xs text-muted-foreground">
                  <strong>Note:</strong> Checkr integration will be enabled soon for automated background verification.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
